import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  IconX,
  IconCheck,
  IconAlertTriangle,
  IconCreditCard,
  IconArrowLeft,
  IconChevronRight,
  IconCash,
  IconBuildingBank,
  IconQrcode,
  IconArrowsSplit,
  IconAlertCircle,
  IconUser,
  IconPhone,
} from '@tabler/icons-react';
import { terbilang } from '../../utils/number';
import { CurrencyInput } from '../../utils/CurrencyInput';
import BankLogo from '../../components/BankLogo';
import { useMasterData } from '../../context/MasterDataContext';

export default function ModalKonfirmasiPenjualan({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  item = null,
  nominalHargaJual = 0,
  paymentMode = 'single',
  singleMethod = 'cash',
  singleBank = '',
  setSingleBank = () => {},
  switchToSingle = () => {},
  switchToSplit = () => {},
  splitMethods = [],
  handleSplitChange = () => {},
  handleFillRemaining = () => {},
  handleResetSplit = () => {},
  bankList = [],
  defaultBank = 'BCA',
  activePayments = [],
  sisaSplit = 0,
  totalSplit = 0,
  src = '',
  namaPembeli = '',
  setNamaPembeli = () => {},
  noHp = '',
  setNoHp = () => {},
  paymentMethods: propPaymentMethods,
}) {
  const masterData = useMasterData?.() || {};
  const paymentMethods = propPaymentMethods || masterData.paymentMethods || { cash: true, transfer: true, qris: true, edc: true };
  const enabledCount = ['cash', 'transfer', 'qris', 'edc'].filter((k) => paymentMethods[k] !== false).length;

  const initialFocusRef = useRef(null);

  function numberWithCommas(x) {
    if (x === null || x === undefined || x === '') return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }


  const beratVal = parseFloat(item?.berat || 0);
  const hargaPerGram = beratVal > 0 && nominalHargaJual > 0 ? Math.round(nominalHargaJual / beratVal) : 0;
  const hargaModalVal = Number(item?.harga_modal || 0);

  // Deteksi Anomali
  const isBelowModal = hargaModalVal > 0 && nominalHargaJual < hargaModalVal;
  const isSuspiciouslyLow = beratVal > 0 && hargaPerGram < 350000;
  const isSuspiciouslyHigh = beratVal > 0 && hargaPerGram > 3000000;
  const hasAnomaly = isBelowModal || isSuspiciouslyLow || isSuspiciouslyHigh;

  // Validasi Submit
  const isSplitValid = paymentMode === 'split' ? sisaSplit === 0 && totalSplit === nominalHargaJual : true;
  const isBankValid = (paymentMode === 'single' && (singleMethod === 'transfer' || singleMethod === 'edc')) ? Boolean(singleBank) : true;
  const canSubmit = !isSubmitting && nominalHargaJual > 0 && isSplitValid && isBankValid;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" initialFocus={initialFocusRef} onClose={isSubmitting ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-xs" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2.5 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                ref={initialFocusRef}
                tabIndex={-1}
                className="w-full max-w-xl max-h-[92vh] transform overflow-hidden rounded-2xl bg-white dark:bg-[#131b2e] text-left align-middle shadow-2xl transition-all border border-slate-200 dark:border-slate-800 flex flex-col my-auto relative focus:outline-none font-sans"
              >
                
                {/* TOP HEADER: PILIH METODE PEMBAYARAN (NAIK KE ATAS) */}
                <div className="px-4 sm:px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0 bg-white dark:bg-[#131b2e] pr-12">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                      <IconCreditCard size={17} />
                    </div>
                    <Dialog.Title as="h3" className="font-extrabold text-xs sm:text-sm text-slate-800 dark:text-slate-100 tracking-wide uppercase">
                      PILIH METODE PEMBAYARAN
                    </Dialog.Title>
                  </div>
                </div>

                {/* Tombol Tutup (X) di Pojok Kanan Atas */}
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="absolute top-2.5 right-3.5 z-20 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer focus:outline-none"
                  title="Tutup Modal"
                >
                  <IconX size={18} />
                </button>

                {/* TOP FIXED SECTION: TOTAL PEMBAYARAN PELANGGAN (JANGAN DISCROLL) */}
                <div className="p-4 sm:p-5 pb-3 space-y-2.5 shrink-0 bg-white dark:bg-[#131b2e] border-b border-slate-100 dark:border-slate-800/80">
                  {/* HERO: TOTAL HARGA DEAL & TERBILANG & RATA-RATA/GRAM (JANGAN DISCROLL) */}
                  <div className="p-3 sm:p-3.5 rounded-xl bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent border border-amber-200 dark:border-amber-900/50 text-center space-y-1 shadow-xs">
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                      Total Pembayaran Pelanggan
                    </span>
                    <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                      Rp {numberWithCommas(nominalHargaJual)}
                    </div>
                    {/* Kalimat Terbilang Otomatis */}
                    <div className="pt-0.5 border-t border-amber-200/60 dark:border-amber-800/40 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200">
                      &ldquo;{terbilang(nominalHargaJual)}&rdquo;
                    </div>
                    {beratVal > 0 && (
                      <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-white/80 dark:bg-slate-800/80 border border-amber-200 dark:border-amber-800/50 text-[11px] text-slate-600 dark:text-slate-300 mt-0.5 shadow-2xs">
                        <span>Harga Rata-rata:</span>
                        <span className="font-black text-amber-600 dark:text-amber-400">
                          Rp {numberWithCommas(hargaPerGram)}
                        </span>
                        <span className="text-slate-400">/ gram</span>
                      </div>
                    )}
                  </div>

                  {/* ANOMALY ALERT BANNER (Hanya jika terdeteksi janggal) */}
                  {hasAnomaly && (
                    <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 space-y-1 text-rose-800 dark:text-rose-300">
                      <div className="flex items-center gap-1.5 font-bold text-xs">
                        <IconAlertTriangle size={15} className="text-rose-600 dark:text-rose-400 shrink-0" />
                        <span>PERHATIAN: Peringatan Harga Janggal!</span>
                      </div>
                      <ul className="text-[11px] space-y-0.5 list-disc list-inside text-rose-700 dark:text-rose-300">
                        {isSuspiciouslyLow && (
                          <li>
                            Harga per gram (<strong>Rp {numberWithCommas(hargaPerGram)}/gr</strong>) SANGAT RENDAH! Periksa apakah kurang mengetik angka nol.
                          </li>
                        )}
                        {isSuspiciouslyHigh && (
                          <li>
                            Harga per gram (<strong>Rp {numberWithCommas(hargaPerGram)}/gr</strong>) SANGAT TINGGI! Periksa apakah kelebihan mengetik angka nol.
                          </li>
                        )}
                        {isBelowModal && (
                          <li>
                            Harga jual (<strong>Rp {numberWithCommas(nominalHargaJual)}</strong>) berada di bawah harga modal (<strong>Rp {numberWithCommas(hargaModalVal)}</strong>)!
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* MIDDLE SCROLLABLE SECTION: PILIH METODE PEMBAYARAN (BERUPA SCROLL) */}
                <div className="p-4 sm:p-5 flex-1 min-h-0 overflow-y-auto space-y-3 custom-scrollbar">
                  {/* SECTION: DATA PEMBELI / MEMBER */}
                  <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200">
                        <IconUser size={16} className="text-amber-500" />
                        <span>Data Pembeli / Member</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-medium">Opsional (Otomatis dibuat Member)</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {/* Input Nama Pembeli */}
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                          <IconUser size={15} />
                        </div>
                        <input
                          type="text"
                          value={namaPembeli}
                          onChange={(e) => setNamaPembeli(e.target.value)}
                          placeholder="Nama Pembeli"
                          className="w-full pl-9 pr-3 py-2 text-xs font-sans font-semibold rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                        />
                      </div>

                      {/* Input No HP */}
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                          <IconPhone size={15} />
                        </div>
                        <input
                          type="tel"
                          value={noHp}
                          onChange={(e) => setNoHp(e.target.value)}
                          placeholder="No. HP / WhatsApp"
                          className="w-full pl-9 pr-3 py-2 text-xs font-sans font-semibold rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                      {/* CARD 1: CASH */}
                      {paymentMethods?.cash !== false && (() => {
                        const isSelected = paymentMode === 'single' && singleMethod === 'cash';
                        return (
                          <div
                            onClick={() => switchToSingle('cash')}
                            className={`rounded-xl border transition-all cursor-pointer overflow-hidden ${
                              isSelected
                                ? 'bg-amber-50/40 dark:bg-amber-950/20 border-amber-500 ring-2 ring-amber-500/20 shadow-xs'
                                : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                          >
                            <div className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                  isSelected ? 'bg-amber-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                }`}>
                                  <IconCash size={19} />
                                </div>
                                <div>
                                  <div className="font-bold text-slate-800 dark:text-slate-100 text-xs sm:text-sm">
                                    CASH (Uang Tunai)
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                                      Pembayaran uang tunai langsung di kasir
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isSelected ? (
                                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                                    <IconCheck size={13} stroke={3} />
                                  </span>
                                ) : (
                                  <IconChevronRight size={17} className="text-slate-400" />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* CARD 2: BANK TRANSFER */}
                      {paymentMethods?.transfer !== false && (() => {
                        const isSelected = paymentMode === 'single' && singleMethod === 'transfer';
                        return (
                          <div
                            onClick={() => {
                              switchToSingle('transfer');
                              if (!singleBank && bankList.length > 0) {
                                setSingleBank(bankList[0]);
                              }
                            }}
                            className={`rounded-xl border transition-all cursor-pointer overflow-hidden ${
                              isSelected
                                ? 'bg-blue-50/40 dark:bg-blue-950/20 border-blue-500 ring-2 ring-blue-500/20 shadow-xs'
                                : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                          >
                            <div className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                  isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                }`}>
                                  <IconBuildingBank size={19} />
                                </div>
                                <div>
                                  <div className="font-bold text-slate-800 dark:text-slate-100 text-xs sm:text-sm flex items-center gap-1.5">
                                    <span>Bank Transfer</span>
                                    {isSelected && singleBank && (
                                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-bold">
                                        {singleBank}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    {/* <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                                      Transfer rekening toko (klik langsung logo bank di bawah)
                                    </span> */}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isSelected ? (
                                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                                    <IconCheck size={13} stroke={3} />
                                  </span>
                                ) : (
                                  <IconChevronRight size={17} className="text-slate-400" />
                                )}
                              </div>
                            </div>

                            {/* Bank Badges Row (Dynamic from bankList) - Langsung bisa dipilih */}
                            <div className="px-3 pb-2.5 pt-2 flex flex-wrap items-center gap-2 border-t border-slate-100/80 dark:border-slate-700/50">
                              {bankList.map((bank) => {
                                const isBankActive = isSelected && singleBank === bank;
                                return (
                                  <button
                                    key={bank}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      switchToSingle('transfer');
                                      setSingleBank(bank);
                                    }}
                                    className={`px-2.5 py-1 text-xs font-bold rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 ${
                                      isBankActive
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm ring-2 ring-blue-500/30'
                                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-slate-700'
                                    }`}
                                  >
                                    <BankLogo name={bank} size="xs" className="h-4.5" />
                                    {isBankActive && <IconCheck size={12} stroke={3} className="text-white ml-0.5" />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* CARD 3: QRIS */}
                      {paymentMethods?.qris !== false && (() => {
                        const isSelected = paymentMode === 'single' && singleMethod === 'qris';
                        return (
                          <div
                            onClick={() => switchToSingle('qris')}
                            className={`rounded-xl border transition-all cursor-pointer overflow-hidden ${
                              isSelected
                                ? 'bg-red-50/40 dark:bg-red-950/20 border-red-500 ring-2 ring-red-500/20 shadow-xs'
                                : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                          >
                            <div className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                  isSelected ? 'bg-red-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                }`}>
                                  <IconQrcode size={19} />
                                </div>
                                <div>
                                  <div className="font-bold text-slate-800 dark:text-slate-100 text-xs sm:text-sm flex items-center gap-1.5">
                                    <span>QRIS</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                                      Pembayaran non-tunai via QRIS
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isSelected ? (
                                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                                    <IconCheck size={13} stroke={3} />
                                  </span>
                                ) : (
                                  <IconChevronRight size={17} className="text-slate-400" />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* CARD 4: DEBIT CARD / EDC */}
                      {paymentMethods?.edc !== false && (() => {
                        const isSelected = paymentMode === 'single' && singleMethod === 'edc';
                        return (
                          <div
                            onClick={() => {
                              switchToSingle('edc');
                              if (!singleBank && bankList.length > 0) {
                                setSingleBank(bankList[0]);
                              }
                            }}
                            className={`rounded-xl border transition-all cursor-pointer overflow-hidden ${
                              isSelected
                                ? 'bg-purple-50/40 dark:bg-purple-950/20 border-purple-500 ring-2 ring-purple-500/20 shadow-xs'
                                : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                          >
                            <div className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                  isSelected ? 'bg-purple-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                }`}>
                                  <IconCreditCard size={19} />
                                </div>
                                <div>
                                  <div className="font-bold text-slate-800 dark:text-slate-100 text-xs sm:text-sm flex items-center gap-1.5">
                                    <span>Kartu Debit / Mesin EDC</span>
                                    {isSelected && singleBank && (
                                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/60 text-purple-700 dark:text-purple-300 font-bold">
                                        {singleBank}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    {/* <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                                      Gesek / Dip di mesin EDC (klik langsung logo bank di bawah)
                                    </span> */}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isSelected ? (
                                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                                    <IconCheck size={13} stroke={3} />
                                  </span>
                                ) : (
                                  <IconChevronRight size={17} className="text-slate-400" />
                                )}
                              </div>
                            </div>

                            {/* Card Badges & EDC Bank Row - Langsung bisa dipilih */}
                            <div className="px-3 pb-2.5 pt-2 flex flex-wrap items-center gap-2 border-t border-slate-100/80 dark:border-slate-700/50">
                              {bankList.map((bank) => {
                                const isBankActive = isSelected && singleBank === bank;
                                return (
                                  <button
                                    key={bank}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      switchToSingle('edc');
                                      setSingleBank(bank);
                                    }}
                                    className={`px-2.5 py-1 text-xs font-bold rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 ${
                                      isBankActive
                                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm ring-2 ring-purple-500/30'
                                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50/50 dark:hover:bg-slate-700'
                                    }`}
                                  >
                                    <BankLogo name={bank} size="xs" className="h-4.5" />
                                    {isBankActive && <IconCheck size={12} stroke={3} className="text-white ml-0.5" />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* CARD 5: SPLIT PAYMENT */}
                      {enabledCount > 1 && (() => {
                        const isSelected = paymentMode === 'split';
                        return (
                          <div
                            onClick={switchToSplit}
                            className={`rounded-xl border transition-all cursor-pointer overflow-hidden ${
                              isSelected
                                ? 'bg-amber-50/40 dark:bg-amber-950/20 border-amber-500 ring-2 ring-amber-500/20 shadow-xs'
                                : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                          >
                            <div className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                  isSelected ? 'bg-amber-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                }`}>
                                  <IconArrowsSplit size={19} />
                                </div>
                                <div>
                                  <div className="font-bold text-slate-800 dark:text-slate-100 text-xs sm:text-sm">
                                    Split (Bagi Pembayaran)
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                                      Kombinasi 2 atau lebih metode (cth: Sebagian Cash, Sebagian Transfer)
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isSelected ? (
                                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                                    <IconCheck size={13} stroke={3} />
                                  </span>
                                ) : (
                                  <IconChevronRight size={17} className="text-slate-400" />
                                )}
                              </div>
                            </div>
                            {/* Expanded Split Breakdown Editor when Selected */}
                            {isSelected && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                className="p-3 bg-amber-50/70 dark:bg-amber-950/30 border-t border-amber-200 dark:border-amber-900/50 space-y-2.5"
                              >
                                {/* Split Status Indicator */}
                                <div className="flex items-center justify-between text-xs px-2 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-800/60">
                                  <span className="text-slate-600 dark:text-slate-300 font-semibold text-[11px]">
                                    Terbagi: <strong className="text-slate-800 dark:text-white">Rp {numberWithCommas(totalSplit)}</strong> / <span>Rp {numberWithCommas(nominalHargaJual)}</span>
                                  </span>
                                  <span className={`font-black text-xs px-2 py-0.5 rounded ${
                                    sisaSplit === 0
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                                      : sisaSplit > 0
                                      ? 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300'
                                      : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                                  }`}>
                                    {sisaSplit === 0
                                      ? 'PAS (Lunas) ✓'
                                      : sisaSplit > 0
                                      ? `Kurang Rp ${numberWithCommas(sisaSplit)}`
                                      : `Lebih Rp ${numberWithCommas(Math.abs(sisaSplit))}`}
                                  </span>
                                </div>

                                {/* List of Split Rows */}
                                <div className="space-y-2">
                                  {splitMethods
                                    .filter((m) => paymentMethods?.[m.id] !== false)
                                    .map((method) => {
                                      const otherTotal = splitMethods
                                        .filter((p) => paymentMethods?.[p.id] !== false)
                                        .reduce(
                                          (sum, p) => (p.id === method.id ? sum : sum + (parseInt(p.nominal) || 0)),
                                          0
                                        );
                                      const remainingForThis = nominalHargaJual - otherTotal;
                                      const inputId = `split-input-${method.id}`;

                                      return (
                                        <div
                                          key={method.id}
                                          className="flex flex-col sm:flex-row sm:items-center gap-1.5 p-2 rounded-lg bg-white dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700"
                                        >
                                        <div className="flex items-center justify-between sm:w-44 shrink-0">
                                          <label
                                            htmlFor={inputId}
                                            className="font-bold text-slate-700 dark:text-slate-200 text-sm cursor-pointer select-none flex-1 py-0.5"
                                          >
                                            {method.label}
                                          </label>
                                          {(method.id === 'transfer' || method.id === 'edc') && (
                                            <select
                                              value={method.nama_bank || defaultBank}
                                              onChange={(e) => handleSplitChange(method.id, 'nama_bank', e.target.value)}
                                              className="select select-bordered select-xs w-24 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-white text-[11px] font-sans font-bold rounded"
                                            >
                                              {bankList.map((bank) => (
                                                <option key={bank} value={bank}>{bank}</option>
                                              ))}
                                            </select>
                                          )}
                                        </div>

                                        <div className="flex items-center gap-1.5 flex-1">
                                          <div className="flex-1">
                                            <CurrencyInput
                                              id={inputId}
                                              value={method.nominal}
                                              onFocus={(e) => e.target.select?.()}
                                              onChange={(val) =>
                                                handleSplitChange(method.id, 'nominal', val === undefined ? '' : val)
                                              }
                                              className="input input-bordered input-xs w-full text-right font-sans font-bold bg-white dark:bg-slate-800 dark:border-slate-600 dark:text-white rounded text-sm"
                                              placeholder="0"
                                            />
                                          </div>

                                          {remainingForThis > 0 ? (
                                            <button
                                              type="button"
                                              onClick={() => handleFillRemaining(method.id)}
                                              className="px-2 py-1 text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded transition shrink-0 cursor-pointer shadow-2xs"
                                            >
                                              Isi Sisa
                                            </button>
                                          ) : (parseInt(method.nominal) || 0) > 0 ? (
                                            <button
                                              type="button"
                                              onClick={() => handleResetSplit(method.id)}
                                              className="px-2 py-1 text-[11px] font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded transition shrink-0 cursor-pointer"
                                            >
                                              Reset
                                            </button>
                                          ) : (
                                            <button
                                              type="button"
                                              onClick={() => handleFillRemaining(method.id)}
                                              className="px-2 py-1 text-[11px] text-slate-400 bg-slate-100 dark:bg-slate-700 rounded transition shrink-0 cursor-pointer"
                                            >
                                              Isi Sisa
                                            </button>
                                          )}

                                          {/* Tombol QR removed */}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                </div>

                {/* BOTTOM FIXED SECTION: METODE PEMBAYARAN TERPILIH (JANGAN DISCROLL) */}
                <div className="px-4 sm:px-5 py-3 bg-slate-50/90 dark:bg-slate-900/70 border-t border-slate-200/80 dark:border-slate-800 shrink-0 space-y-1.5">
                  <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 dark:text-slate-400">
                    <span>Metode Pembayaran Terpilih:</span>
                    <span className="text-slate-800 dark:text-slate-200">
                      {paymentMode === 'single' ? '100% Pembayaran' : 'Split Pembayaran'}
                    </span>
                  </div>

                  <div className="space-y-1.5 max-h-24 overflow-y-auto">
                    {paymentMode === 'single' ? (
                      <div className="flex items-center justify-between text-xs py-1.5 px-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700/50">
                        <div className="flex items-center gap-2">
                          {(singleMethod === 'transfer' || singleMethod === 'edc') && singleBank ? (
                            <BankLogo name={singleBank} size="xs" className="h-5 drop-shadow-2xs" />
                          ) : singleMethod === 'qris' ? (
                            <BankLogo name="QRIS" size="xs" className="h-5 drop-shadow-2xs" />
                          ) : null}
                          <span className="font-bold text-slate-800 dark:text-slate-100 uppercase">
                            {singleMethod} {((singleMethod === 'transfer' || singleMethod === 'edc') && singleBank) ? `(${singleBank})` : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900 dark:text-white">
                            Rp {numberWithCommas(nominalHargaJual)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      splitMethods
                        .filter((p) => (parseInt(p.nominal) || 0) > 0)
                        .map((p, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-xs py-1.5 px-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700/50"
                          >
                            <div className="flex items-center gap-2">
                              {(p.id === 'transfer' || p.id === 'edc') && p.nama_bank ? (
                                <BankLogo name={p.nama_bank} size="xs" className="h-4.5 drop-shadow-2xs" />
                              ) : p.id === 'qris' ? (
                                <BankLogo name="QRIS" size="xs" className="h-4.5 drop-shadow-2xs" />
                              ) : null}
                              <span className="font-bold text-slate-800 dark:text-slate-100 uppercase">
                                {p.label} {((p.id === 'transfer' || p.id === 'edc') && p.nama_bank) ? `(${p.nama_bank})` : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900 dark:text-white">
                                Rp {numberWithCommas(p.nominal)}
                              </span>
                            </div>
                          </div>
                        ))
                    )}
                  </div>

                  {paymentMode === 'split' && sisaSplit !== 0 && (
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-rose-600 dark:text-rose-400 pt-0.5">
                      <IconAlertCircle size={14} className="shrink-0" />
                      <span>Selesaikan nominal split hingga pas (Rp 0 sisa) untuk melanjutkan.</span>
                    </div>
                  )}

                  {paymentMode === 'single' && (singleMethod === 'transfer' || singleMethod === 'edc') && !singleBank && (
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-rose-600 dark:text-rose-400 pt-0.5">
                      <IconAlertCircle size={14} className="shrink-0" />
                      <span>Silahkan pilih rekening / mesin bank terlebih dahulu.</span>
                    </div>
                  )}
                </div>

                {/* Footer Buttons */}
                <div className="px-5 py-3.5 bg-slate-50 dark:bg-slate-900/70 border-t border-slate-100 dark:border-slate-800 flex flex-col-reverse sm:flex-row items-center justify-between gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition flex items-center justify-center gap-1 cursor-pointer"
                  >
                    <IconArrowLeft size={15} />
                    <span>Periksa Kembali (Batal)</span>
                  </button>

                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={!canSubmit}
                    className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs sm:text-sm font-bold rounded-xl shadow-md shadow-emerald-600/20 transition cursor-pointer active:scale-98"
                  >
                    {isSubmitting ? (
                      <span>Memproses Transaksi...</span>
                    ) : (
                      <>
                        <IconCheck size={17} stroke={3} />
                        <span>Ya, Selesaikan & Cetak Nota</span>
                      </>
                    )}
                  </button>
                </div>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
